import React, { useEffect } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";

import { fetchCaseAudits } from "../logic/thunks";
import { selectCaseAuditsStatus } from "../logic/slice";
import {
  fetchIncidentDetectionLapseReview,
} from "../../incidentDetectionLapseReview/logic/thunk";
import { selectIncidentDetectionLapseReviewStatus } from "../../incidentDetectionLapseReview/logic/slice";

import DetectionWindowSummaryHeader from "./DetectionWindowSummaryHeader";
import OtherAuditsTable from "./OtherAuditsTable";
import AuditsNonDetectionDetails from "./AuditsNonDetectionDetails";

const OtherAuditsPage: React.FC = () => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const auditsStatus = useAppSelector(selectCaseAuditsStatus);
  const reviewStatus = useAppSelector(selectIncidentDetectionLapseReviewStatus);

  useEffect(() => {
    if (caseId) {
      dispatch(fetchCaseAudits(caseId));
    }
  }, [caseId, dispatch]);

  const isLoading =
    auditsStatus === "loading" ||
    auditsStatus === "idle" ||
    reviewStatus === "loading" ||
    reviewStatus === "idle";

  if (isLoading) {
    return (
      <div className="flex min-h-[700px] w-full items-center justify-center rounded-[14px] border border-gray-100 bg-white p-6 shadow-overviewCard">
        <CustomLoadingSpinner
          title="Loading other audits..."
          subtitle="Fetching inspection and audit records for this case."
          minHeight="min-h-0"
          className="m-0 border-0 bg-transparent shadow-none"
        />
      </div>
    );
  }

  if (auditsStatus === "failed" || reviewStatus === "failed") {
    return (
      <div className="flex min-h-[700px] w-full items-center justify-center rounded-[14px] border border-gray-100 bg-white p-6 shadow-overviewCard">
        <div className="w-[60vw] max-w-[800px]">
          <ErrorDisplay
            error="Unable to load other audits. Please try again."
            onRetry={() => {
              if (!caseId) return;
              dispatch(fetchCaseAudits(caseId));
              dispatch(fetchIncidentDetectionLapseReview(caseId));
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex w-full flex-col gap-1">
        <DetectionWindowSummaryHeader />
        <OtherAuditsTable />
      </div>
      <AuditsNonDetectionDetails />
    </div>
  );
};

export default OtherAuditsPage;
